import React, { useEffect, useRef, useState, } from "react";
import { useMemo } from "react";
import { FixedSizeList as List } from 'react-window';

export default function TreeSelect({
    name,
    onValueChange = () => { },
    multiple = false,
    defaultValue,
    showSearch,
    style,
    placeholder = '請選擇',
    allowClear,
    treeDefaultExpandAll = false,
    onChange = () => { },
    treeData = [],
    itemHeight = 36,
    height = 300,
    loadData,
    value,
    ...props
}) {
    const [search, setSearch] = useState('');
    const [expandedKeys, setExpandedKeys] = useState(new Set());
    const [selectedValues, setSelectedValues] = useState(() => {
        if (defaultValue) {
            return Array.isArray(defaultValue) ? defaultValue.map(String) : [String(defaultValue)];
        }
        return [];
    });
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        if (value) {
            if (Array.isArray(value))
                setSelectedValues(value)
            else
                setSelectedValues([value])
        }
    }, [value])
    // 🌟 加上：預設展開全部 tree node
    useEffect(() => {
        if (treeDefaultExpandAll && treeData.length > 0) {
            const collectAllKeys = (nodes) => {
                let keys = [];
                for (const node of nodes) {
                    keys.push(node.value);
                    if (node.children) {
                        keys = keys.concat(collectAllKeys(node.children));
                    }
                }
                return keys;
            };
            const allKeys = collectAllKeys(treeData);
            setExpandedKeys(new Set(allKeys));
        }
    }, [treeData, treeDefaultExpandAll]);

    useEffect(() => {
        if (Array.isArray(defaultValue) && defaultValue.length === 0) {
            onChange({ target: { name, value: multiple ? [] : undefined } });
            onValueChange({ target: { name, value: multiple ? [] : undefined } });
        }
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const flattenTree = (nodes, level = 0, parentExpanded = true) => {
        let result = [];
        for (const node of nodes) {
            const matched = String(node.title || '').toLowerCase().includes(search.toLowerCase());
            const visible = parentExpanded && (search ? matched : true);

            result.push({ ...node, level, visible });

            if (node.children && expandedKeys.has(node.value)) {
                result = result.concat(flattenTree(node.children, level + 1, visible));
            }
        }
        return result;
    };

    const flatNodes = useMemo(() => flattenTree(treeData), [treeData, expandedKeys, search]);
    const visibleNodes = flatNodes.filter((node) => node.visible);

    const handleToggleExpand = async (value) => {
        if (loadData) {
            await loadData({ value });   // 等候資料回來
        }
        setExpandedKeys((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(value)) {
                newSet.delete(value);
            } else {
                newSet.add(value);
            }
            return newSet;
        });
    };

    const handleSelect = (value) => {
        let newSelected;
        if (multiple) {
            if (selectedValues.includes(value)) {
                newSelected = selectedValues.filter((v) => v !== value);
            } else {
                newSelected = [...selectedValues, value];
            }
        } else {
            newSelected = [value];
        }
        setSelectedValues(newSelected);

        const returnValue = multiple ? newSelected : (newSelected.length > 0 ? newSelected[0] : undefined);

        onChange({ target: { name, value: returnValue } });
        onValueChange({ target: { name, value: returnValue } });

        if (!multiple) setIsDropdownOpen(false);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        setSelectedValues([]);
        onChange({ target: { name, value: multiple ? [] : undefined } });
        onValueChange({ target: { name, value: multiple ? [] : undefined } });
    };

    const selectedLabels = () => {
        const findLabels = (nodes, selected) => {
            let labels = [];
            for (const node of nodes) {
                if (selected.includes(node.value)) labels.push(node.title);
                if (node.children) labels = labels.concat(findLabels(node.children, selected));
            }
            return labels;
        };
        return findLabels(treeData, selectedValues).join(', ') || placeholder;
    };

    const Row = ({ index, style: rowStyle }) => {
        const node = visibleNodes[index];
        if (!node) return null;

        return (
            <div
                style={{
                    ...rowStyle,
                    paddingLeft: `${node.level * 16}px`,
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: selectedValues.includes(node.value) ? '#f0f9ff' : 'white',
                    cursor: 'pointer',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f9ff')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = selectedValues.includes(node.value) ? '#f0f9ff' : 'white')}
            >
                {node.children && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleToggleExpand(node.value);
                        }}
                        style={{ marginRight: 8, background: 'none', border: 'none', cursor: 'pointer', fontSize: '3.5vmin' }}
                    >
                        {expandedKeys.has(node.value) ? '▾' : '▸'}
                    </button>
                )}
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {multiple ? (
                        <input
                            type="checkbox"
                            checked={selectedValues.includes(node.value)}
                            onChange={() => handleSelect(node.value)}
                        />
                    ) : (
                        <input
                            type="radio"
                            name={name}
                            checked={selectedValues.includes(node.value)}
                            onChange={() => handleSelect(node.value)}
                        />
                    )}
                    <span>{node.title}</span>
                </label>
            </div>
        );
    };

    return (
        <div style={{ position: 'relative', ...style }} ref={dropdownRef}>
            <div
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                style={{
                    border: '1px solid #d1d5db',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    userSelect: 'none',
                }}
            >
                <span style={{ color: selectedValues.length ? '#000' : '#9ca3af' }}>{selectedLabels()}</span>
                {allowClear && selectedValues.length > 0 ? (
                    <button
                        onClick={handleClear}
                        style={{ marginLeft: '8px', background: 'none', border: 'none', cursor: 'pointer', color: '#f87171', fontWeight: 'bold' }}
                    >
                        ×
                    </button>
                ) : (
                    <span style={{ marginLeft: '8px' }}>{isDropdownOpen ? '▲' : '▼'}</span>
                )}
            </div>

            {isDropdownOpen && (
                <div
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 'auto',            // 讓寬度不被強制撐滿父層
                        zIndex: 1000,
                        backgroundColor: '#fff',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
                        padding: '8px',
                        minWidth: '100%',         // 最少跟 trigger 一樣寬
                        width: 'auto',            // 向內容撐開
                        whiteSpace: 'nowrap',     // 不讓文字內部自行換行
                        overflowX: 'auto',        // 超過寬度就水平滾動
                    }}
                >
                    {showSearch && (
                        <input
                            type="text"
                            value={search}
                            placeholder="搜尋..."
                            onChange={(e) => setSearch(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '6px 10px',
                                marginBottom: '8px',
                                borderRadius: '6px',
                                border: '1px solid #d1d5db',
                                outline: 'none',
                                fontSize: '14px',
                            }}
                        />
                    )}
                    <List
                        height={height}
                        itemCount={visibleNodes.length}
                        itemSize={itemHeight}
                        width="100%"
                    >
                        {Row}
                    </List>
                </div>
            )}
        </div>
    );
};